import { useRef, useState } from "react";
import { Upload, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function CsvImportButton({ entity, label, onImported }) {
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/contractor/import/${entity}/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity}-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/contractor/import/${entity}`, formData);
      setResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} row${data.imported === 1 ? "" : "s"}`);
      if (data.errors.length > 0) toast.error(`${data.errors.length} row${data.errors.length === 1 ? "" : "s"} had errors`);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { reset(); setShow(true); }}
        data-testid={`import-${entity}-button`}
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-[13px] font-semibold"
      >
        <Upload size={14} /> Import CSV
      </button>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid={`import-${entity}-modal`}>
          <DialogHeader><DialogTitle>Import {label} from CSV</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <button onClick={downloadTemplate} data-testid={`download-template-${entity}`} className="flex items-center gap-1.5 text-[12.5px] font-semibold text-sky-600">
              <Download size={13} /> Download CSV template
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid={`import-file-input-${entity}`}
              className="w-full text-[13px]"
            />
            <button onClick={upload} disabled={busy} data-testid={`confirm-import-${entity}`} className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-50">
              {busy ? "Importing…" : "Upload & Import"}
            </button>
            {result && (
              <div className="text-[12.5px] border-t border-slate-100 pt-3 mt-1" data-testid={`import-result-${entity}`}>
                <div className="font-semibold text-slate-700">
                  {result.imported} imported, {result.errors.length} error{result.errors.length === 1 ? "" : "s"}
                </div>
                {result.errors.length > 0 && (
                  <ul className="mt-1.5 max-h-40 overflow-y-auto ss-scrollbar space-y-1 text-red-600">
                    {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
